import { z } from 'zod'
import { templateInfo } from './templates'
import type {
  ClientAutomation,
  DropTarget,
  InputSlot,
  PrefixTarget,
  ProblemFile,
  ResultFile,
  ResultsTarget,
  RunSummary,
  TemplateId
} from './types'

/**
 * Pure logic for turning raw GCP resources into the shapes the app shows, and
 * for resolving where each automation reads input files and writes results.
 * Free of network imports so everything here is unit tested.
 *
 * Folder defaults must match the runner's DEFAULTS in the admin repo — the
 * admin app omits params left at their default, so the client re-applies the
 * same fallbacks to find the right folders.
 */

const TEMPLATE_IDS: readonly TemplateId[] = [
  'website-uptime',
  'gcs-backup',
  'gcs-cleanup',
  'http-task',
  'file-process',
  'python-script'
]

function isTemplateId(value: unknown): value is TemplateId {
  return typeof value === 'string' && (TEMPLATE_IDS as readonly string[]).includes(value)
}

// --- Cloud Run job parsing ---

interface RawRunJob {
  name?: string
  labels?: Record<string, string>
  template?: {
    template?: {
      containers?: Array<{ env?: Array<{ name?: string; value?: string }> }>
    }
  }
}

const paramsSchema = z.record(z.union([z.string(), z.number(), z.boolean()]))

/**
 * Parses one Cloud Run Job into a ClientAutomation, or null when the job is
 * not an Autom8-managed job (or is malformed). Name/schedule/paused are
 * filled in later from the matching Cloud Scheduler job.
 */
export function parseRunJob(job: unknown): ClientAutomation | null {
  const raw = job as RawRunJob
  if (!raw || typeof raw !== 'object') return null
  if (raw.labels?.['managed-by'] !== 'autom8') return null

  const jobId = (raw.name ?? '').split('/').pop() ?? ''
  if (!jobId) return null

  const env = raw.template?.template?.containers?.[0]?.env ?? []
  const byName = new Map(env.map((e) => [e.name, e.value] as const))
  const templateId = byName.get('AUTOM8_TEMPLATE')
  if (!isTemplateId(templateId)) return null

  let params: Record<string, string | number | boolean> = {}
  const rawParams = byName.get('AUTOM8_PARAMS')
  if (rawParams) {
    try {
      params = paramsSchema.parse(JSON.parse(rawParams))
    } catch {
      return null
    }
  }

  const info = templateInfo(templateId)
  return {
    jobId,
    name: info?.title ?? templateId,
    templateId,
    params,
    paused: null,
    acceptsFiles: dropTargetsFor(templateId, params).length > 0,
    deletesAfterDownload: deletesAfterDownload(templateId, params)
  }
}

/** Extracts the friendly name from a scheduler description `Autom8: <name>`. */
export function nameFromSchedulerDescription(description: string | undefined): string | undefined {
  if (!description) return undefined
  const match = /^Autom8:\s*(.+)$/.exec(description.trim())
  return match?.[1]?.trim() || undefined
}

// --- Executions ---

interface RawExecution {
  name?: string
  createTime?: string
  completionTime?: string
  failedCount?: number
  cancelledCount?: number
}

export function executionSummary(execution: unknown): RunSummary {
  const raw = (execution ?? {}) as RawExecution
  const id = (raw.name ?? '').split('/').pop() || 'unknown'
  const completed = !!raw.completionTime
  const failed = (raw.failedCount ?? 0) > 0 || (raw.cancelledCount ?? 0) > 0
  return {
    id,
    ...(raw.createTime ? { startedAt: raw.createTime } : {}),
    ...(raw.completionTime ? { completedAt: raw.completionTime } : {}),
    state: !completed ? 'running' : failed ? 'failed' : 'succeeded'
  }
}

// --- Input / output locations ---

function strParam(
  params: Record<string, string | number | boolean>,
  key: string
): string | undefined {
  const v = params[key]
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined
}

/** Normalizes a folder prefix to always end with exactly one `/`. */
function asPrefix(value: string): string {
  return value.replace(/\/+$/, '') + '/'
}

/** Runner defaults, mirrored from the admin repo's runner handlers. */
const DEFAULTS = {
  filesIncoming: 'autom8/files/incoming/',
  filesOutput: 'autom8/files/processed/',
  filesMaster: 'autom8/files/master.csv',
  // Deliberately not the file-process folders: two automations sharing one
  // incoming folder would each eat the other's files.
  pythonIncoming: 'autom8/python/incoming/',
  pythonOutput: 'autom8/python/processed/',
  filesError: 'autom8/files/error/',
  pythonError: 'autom8/python/error/',
  filesArchive: 'autom8/files/archive/',
  pythonArchive: 'autom8/python/archive/'
}

/** Archive folder per template, where a processed original is kept. */
const DEFAULT_ARCHIVE: Record<string, string> = {
  'file-process': DEFAULTS.filesArchive,
  'python-script': DEFAULTS.pythonArchive
}

/** Error folder per template, for the ones that set files aside on failure. */
const DEFAULT_ERROR: Record<string, string> = {
  'file-process': DEFAULTS.filesError,
  'python-script': DEFAULTS.pythonError
}

/** Incoming folder per template when the admin left the param at its default. */
const DEFAULT_INCOMING: Record<string, string> = {
  'file-process': DEFAULTS.filesIncoming,
  'python-script': DEFAULTS.pythonIncoming
}

/**
 * Slot ids become folder names and are compared against upload requests, so
 * they are held to the same shape everywhere: lowercase, no dots, no slashes.
 */
const slotIdPattern = /^[a-z][a-z0-9-]{0,30}$/

const inputSlotSchema = z.object({
  id: z.string().regex(slotIdPattern),
  label: z.string().min(1).max(60),
  help: z.string().max(200).optional(),
  accept: z.array(z.string()).min(1).optional(),
  required: z.boolean().optional()
})

/** At most this many named inputs; more is a misconfiguration, not a use case. */
const MAX_SLOTS = 8

const inputsSchema = z.array(inputSlotSchema).min(1).max(MAX_SLOTS)

/**
 * The named inputs an administrator configured, or null when there are none.
 *
 * The value is a JSON string in the flat params bag, so it arrives here as
 * whatever was deployed — possibly from a newer admin app than this build
 * knows about. Anything that does not parse, or that repeats an id, is treated
 * as no slots at all: falling back to the single unnamed folder is the
 * behaviour every automation had before this existed, and it keeps files
 * reaching a folder the runner reads rather than one nothing does.
 */
export function inputSlotsFor(
  params: Record<string, string | number | boolean>
): InputSlot[] | null {
  const raw = strParam(params, 'inputs')
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  const result = inputsSchema.safeParse(parsed)
  if (!result.success) return null

  const ids = new Set<string>()
  for (const slot of result.data) {
    if (ids.has(slot.id)) return null
    ids.add(slot.id)
  }
  return result.data
}

/**
 * Where dropped files should be uploaded for this automation: one target per
 * named input, or a single unnamed one when none are configured. Empty when
 * the template takes no file input, or the bucket param is missing.
 *
 * Slots each get their own subfolder under the incoming prefix. Putting the
 * routing in the object path rather than in the file name means it survives a
 * rename, is visible in the Cloud console, and needs nothing new from the
 * archive and error paths, which already key off the path.
 */
export function dropTargetsFor(
  templateId: string,
  params: Record<string, string | number | boolean>
): DropTarget[] {
  const info = templateInfo(templateId)
  if (!info?.fileDrop) return []
  const bucket = strParam(params, 'bucket')
  if (!bucket) return []

  const defaultPrefix = DEFAULT_INCOMING[templateId] ?? DEFAULTS.filesIncoming
  const prefix = asPrefix(strParam(params, 'incomingPrefix') ?? defaultPrefix)
  const slots = inputSlotsFor(params)
  if (!slots) {
    return [
      {
        bucket,
        prefix,
        accept: info.fileDrop.accept,
        prompt: info.fileDrop.prompt,
        slotId: null,
        required: true
      }
    ]
  }

  return slots.map((slot) => ({
    bucket,
    prefix: `${prefix}${slot.id}/`,
    // A slot may narrow what the template can parse, never widen it: the
    // runner would reject the extra extension anyway, and offering it in the
    // picker would only produce a file that fails later.
    accept: slot.accept
      ? slot.accept.filter((ext) => info.fileDrop!.accept.includes(ext))
      : info.fileDrop!.accept,
    prompt: `Drop ${slot.label} here`,
    slotId: slot.id,
    label: slot.label,
    ...(slot.help ? { help: slot.help } : {}),
    required: slot.required !== false
  }))
}

/**
 * The one target an upload naming `slotId` may go to, or null when there is no
 * such input. `null` asks for the unnamed single input, which only exists when
 * the automation has no named ones.
 *
 * The lookup lives here rather than in either caller so the desktop-era rule
 * still holds: whoever is uploading names an automation and an input, never a
 * bucket or a path.
 */
export function dropTargetForSlot(
  templateId: string,
  params: Record<string, string | number | boolean>,
  slotId: string | null
): DropTarget | null {
  return dropTargetsFor(templateId, params).find((t) => t.slotId === slotId) ?? null
}

/**
 * Where this automation's outputs can be found, or null when it produces
 * nothing downloadable (alerts, backups, cleanups).
 */
export function resultsTargetFor(
  templateId: string,
  params: Record<string, string | number | boolean>
): ResultsTarget | null {
  const bucket = strParam(params, 'bucket')
  if (!bucket) return null

  switch (templateId) {
    case 'file-process': {
      if (strParam(params, 'outputMode') === 'append-to-master') {
        return { bucket, kind: 'object', object: strParam(params, 'masterObject') ?? DEFAULTS.filesMaster }
      }
      return { bucket, kind: 'prefix', prefix: asPrefix(strParam(params, 'outputPrefix') ?? DEFAULTS.filesOutput) }
    }
    case 'python-script':
      // Whatever the script wrote, under one folder — there is no master-file
      // mode here, because the client cannot know what the script produces.
      return {
        bucket,
        kind: 'prefix',
        prefix: asPrefix(strParam(params, 'outputPrefix') ?? DEFAULTS.pythonOutput)
      }
    default:
      return null
  }
}

/**
 * Where files this automation could not process are set aside, or null when
 * the template has no error folder.
 *
 * Kept apart from `resultsTargetFor` on purpose: these are not results, and
 * listing a rejected file among the outputs would read to a business user as
 * though it were one. The suffix is what the runner writes beside a failed
 * file — see the python-script handler in the admin repo.
 */
export function problemsTargetFor(
  templateId: string,
  params: Record<string, string | number | boolean>
): ResultsTarget | null {
  const bucket = strParam(params, 'bucket')
  const fallback = DEFAULT_ERROR[templateId]
  if (!bucket || !fallback) return null
  return { bucket, kind: 'prefix', prefix: asPrefix(strParam(params, 'errorPrefix') ?? fallback) }
}

/**
 * Custom metadata key naming the archived input an output was made from.
 *
 * Written by the runner in the admin repo (`runner/src/storage.ts`), which
 * hard-codes the same string. An output written before the runner recorded
 * this — or a master CSV, which is deliberately never stamped — simply has
 * no source, and its input is then left alone rather than guessed at.
 */
export const SOURCE_METADATA_KEY = 'autom8-source'

/**
 * Where this automation keeps originals it has finished with, or null when
 * the template does not archive.
 *
 * Never shown to anyone: it exists so a deletion can be checked against the
 * automation's own archive folder before it happens.
 */
export function archiveTargetFor(
  templateId: string,
  params: Record<string, string | number | boolean>
): PrefixTarget | null {
  const bucket = strParam(params, 'bucket')
  const fallback = DEFAULT_ARCHIVE[templateId]
  if (!bucket || !fallback) return null
  return { bucket, kind: 'prefix', prefix: asPrefix(strParam(params, 'archivePrefix') ?? fallback) }
}

/**
 * Whether downloading a result from this automation should delete it and the
 * input it came from — the administrator's `deleteAfterDownload` checkbox.
 *
 * Refused for append-to-master, whatever the checkbox says: that output is one
 * CSV holding every processed file's rows, so deleting it on one person's
 * download would take everyone else's work with it. The admin app says as much
 * in the field's help text, and enforcing it here as well means an automation
 * switched to master mode after the box was ticked cannot destroy the master.
 */
export function deletesAfterDownload(
  templateId: string,
  params: Record<string, string | number | boolean>
): boolean {
  if (params['deleteAfterDownload'] !== true) return false
  if (!DEFAULT_ARCHIVE[templateId]) return false
  if (templateId === 'file-process' && strParam(params, 'outputMode') === 'append-to-master') {
    return false
  }
  return true
}

/**
 * The archived inputs an output was produced from.
 *
 * The runner stamps one object with `autom8-source`. A single input stays the
 * plain path it has always been; a *set* — several named inputs run together —
 * is stamped as a JSON array. Reading both here is what lets outputs written
 * before named inputs existed keep working unchanged.
 *
 * Anything that does not read cleanly comes back empty, which costs a deletion
 * and never causes one. That is the right direction to fail in: this feeds the
 * only irreversible thing the app does.
 */
export function sourceObjectsOf(file: Pick<ResultFile, 'sourceObject'>): string[] {
  const raw = file.sourceObject?.trim()
  if (!raw) return []
  // Only the array form starts with '[', and an object name that genuinely
  // did would simply not be parsed as one — its input is kept rather than
  // deleted, which is the harmless half of getting this wrong.
  if (!raw.startsWith('[')) return [raw]
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const paths = parsed.filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    // A partly-unreadable array is not half-trusted: one member we cannot
    // read means we do not know what the set was.
    if (paths.length !== parsed.length) return []
    return [...new Set(paths)]
  } catch {
    return []
  }
}

/**
 * The objects to delete now that `downloaded` has been collected: the file
 * itself, and each input it was made from that nothing else still needs.
 *
 * Two things stop an input going too early. One dropped file can produce
 * several outputs — a python script may write a summary and a detail file from
 * one spreadsheet — and deleting the original when the first of them is
 * collected would strand the rest without their source. And one output can
 * come from several inputs, when a script runs over a *set*; each member is
 * then considered on its own, because the other outputs of that run may still
 * point at some of them and not others.
 *
 * Every member is also checked against the archive folder separately. That is
 * the same rule the single-source version applied, applied once per member: an
 * object outside this automation's own archive is not ours to delete, and one
 * odd member does not stop the others being collected.
 *
 * `siblings` is the current listing of the results folder, `downloaded`
 * included; callers that cannot supply one get the output deleted and the
 * inputs kept, which is the safe half.
 */
export function cleanupAfterDownload(
  downloaded: ResultFile,
  siblings: readonly ResultFile[],
  archive: ResultsTarget | null
): string[] {
  const objects = [downloaded.object]
  const sources = sourceObjectsOf(downloaded)
  if (sources.length === 0 || !archive) return objects

  const stillNeeded = new Set<string>()
  for (const sibling of siblings) {
    if (sibling.object === downloaded.object) continue
    for (const source of sourceObjectsOf(sibling)) stillNeeded.add(source)
  }

  for (const source of sources) {
    if (!isResultObject(source, archive)) continue
    if (stillNeeded.has(source)) continue
    objects.push(source)
  }
  return objects
}

/**
 * The storage operations `cleanUpDownloaded` needs, injected rather than
 * imported so the sequence stays testable against a fake store.
 */
export interface CleanupPorts {
  list(bucket: string, prefix: string, max: number): Promise<ResultFile[]>
  remove(bucket: string, object: string): Promise<void>
}

/**
 * How much of the results folder is examined to decide whether an input is
 * still needed. The listing the panels show is capped far lower (newest first),
 * which is fine for reading but not for this: an output missing from the page
 * would look like one output fewer holding on to its input. A listing that
 * comes back at this cap is treated as incomplete — see below.
 */
const MAX_SIBLINGS = 1000

/**
 * Deletes a just-downloaded result and the input it was made from, for an
 * automation the administrator set to keep no copy. Returns what was deleted,
 * which is empty whenever the setting does not apply.
 *
 * Every guard that decides *whether* to delete lives here rather than in the
 * two callers, because this is the one irreversible thing the client app does:
 *
 *  - the automation must have the setting on (`deletesAfterDownload`, which is
 *    itself false for append-to-master),
 *  - results must live under a prefix, never a single fixed object — a shared
 *    master CSV is not one person's to delete,
 *  - the downloaded object must be inside that results prefix, which excludes
 *    the set-aside files in the error folder: those are a diagnostic, and
 *    someone reading one has not collected a result,
 *  - the input must be inside this automation's own archive folder.
 *
 * Callers pass an object they have already checked belongs to the automation;
 * these checks narrow that further rather than replacing it.
 */
export async function cleanUpDownloaded(
  automation: Pick<ClientAutomation, 'templateId' | 'params'>,
  downloadedObject: string,
  ports: CleanupPorts
): Promise<string[]> {
  const { templateId, params } = automation
  if (!deletesAfterDownload(templateId, params)) return []

  const results = resultsTargetFor(templateId, params)
  if (!results || results.kind !== 'prefix') return []
  if (!isResultObject(downloadedObject, results)) return []

  const siblings = await ports.list(results.bucket, results.prefix, MAX_SIBLINGS)
  // Not in the listing means it has already gone, or the folder holds more
  // files than one page: delete the output and leave the input, which is the
  // half that cannot strand anything.
  const downloaded = siblings.find((f) => f.object === downloadedObject) ?? {
    object: downloadedObject,
    name: downloadedObject.split('/').pop() ?? downloadedObject,
    sizeBytes: 0
  }

  // A full page means there may be more outputs than were listed, one of them
  // possibly the other user of this input. Delete only the output.
  const archive = siblings.length >= MAX_SIBLINGS ? null : archiveTargetFor(templateId, params)
  const objects = cleanupAfterDownload(downloaded, siblings, archive)
  for (const object of objects) {
    await ports.remove(results.bucket, object)
  }
  return objects
}

export const EXPLANATION_SUFFIX = '.error.txt'

/**
 * Turns a flat listing of the error folder into one entry per failed file,
 * each carrying the `<name>.error.txt` the runner wrote beside it. An
 * explanation whose file is gone stands on its own rather than disappearing —
 * it is still the only record of what happened.
 */
export function pairProblemFiles(files: readonly ResultFile[]): ProblemFile[] {
  const explanations = new Map<string, ResultFile>()
  for (const f of files) {
    if (f.object.endsWith(EXPLANATION_SUFFIX)) {
      explanations.set(f.object.slice(0, -EXPLANATION_SUFFIX.length), f)
    }
  }
  const present = new Set(files.map((f) => f.object))
  const paired: ProblemFile[] = []
  for (const f of files) {
    // An explanation belongs to the entry for the file it explains, not to one
    // of its own — unless that file is gone, in which case it stands alone as
    // the only remaining record of what happened.
    const explains = f.object.endsWith(EXPLANATION_SUFFIX)
      ? f.object.slice(0, -EXPLANATION_SUFFIX.length)
      : null
    if (explains !== null && present.has(explains)) continue
    const explanation = explanations.get(f.object)
    paired.push({
      object: f.object,
      name: f.name,
      sizeBytes: f.sizeBytes,
      ...(f.updatedAt ? { updatedAt: f.updatedAt } : {}),
      ...(explanation ? { explanationObject: explanation.object } : {})
    })
  }
  return paired
}

/** True when `object` is inside the automation's results target — the only
 *  objects the renderer may ask the main process to download. */
export function isResultObject(object: string, target: ResultsTarget): boolean {
  if (!object) return false
  return target.kind === 'object' ? object === target.object : object.startsWith(target.prefix) && object !== target.prefix
}

// --- File names & content types ---

const MAX_NAME_LENGTH = 120

/**
 * Reduces a user-supplied file name to a safe GCS object basename: strips any
 * path components, replaces control and special characters, and bounds length
 * while preserving the extension.
 */
export function sanitizeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? ''
  let clean = base
    .replace(/[^A-Za-z0-9._ ()-]+/g, '_')
    .replace(/\s+/g, ' ')
    .replace(/^[. ]+|[. ]+$/g, '')
  if (!clean || clean.replace(/[._ ()-]/g, '') === '') return 'file'
  if (clean.length > MAX_NAME_LENGTH) {
    const dot = clean.lastIndexOf('.')
    const ext = dot > 0 ? clean.slice(dot).slice(0, 12) : ''
    clean = clean.slice(0, MAX_NAME_LENGTH - ext.length) + ext
  }
  return clean
}

export function extensionOf(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? ''
  const dot = base.lastIndexOf('.')
  return dot > 0 ? base.slice(dot).toLowerCase() : ''
}

const CONTENT_TYPES: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.gif': 'image/gif',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

export function contentTypeFor(name: string): string {
  return CONTENT_TYPES[extensionOf(name)] ?? 'application/octet-stream'
}

/**
 * Appends a timestamp before the extension, used when an upload would
 * overwrite a file of the same name that is still waiting to be processed.
 */
export function timestampedName(name: string, at: Date): string {
  const stamp =
    at.getFullYear().toString() +
    String(at.getMonth() + 1).padStart(2, '0') +
    String(at.getDate()).padStart(2, '0') +
    '-' +
    String(at.getHours()).padStart(2, '0') +
    String(at.getMinutes()).padStart(2, '0') +
    String(at.getSeconds()).padStart(2, '0')
  const dot = name.lastIndexOf('.')
  return dot > 0 ? `${name.slice(0, dot)}-${stamp}${name.slice(dot)}` : `${name}-${stamp}`
}

// --- Error translation ---

/**
 * Turns a Google Cloud API failure into a sentence a non-technical user can
 * act on. The technical detail stays in the main-process console, not here.
 */
export function friendlyApiError(status: number): string {
  switch (status) {
    case 401:
      return 'Your Google sign-in expired. Please sign in again.'
    case 403:
      return "Your account doesn't have access to this yet. Ask your administrator to check your permissions."
    case 404:
      return "Google Cloud couldn't find that. Refresh and try again — if it keeps happening, tell your administrator."
    case 429:
      return 'Google Cloud is asking us to slow down. Wait a moment and try again.'
    default:
      return status >= 500
        ? 'Google Cloud had a temporary problem. Wait a moment and try again.'
        : 'Google Cloud reported a problem. If it keeps happening, tell your administrator.'
  }
}

// --- Schedules ---

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function timeOfDay(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  const ampm = hour < 12 ? 'AM' : 'PM'
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`
}

/**
 * Describes a 5-field cron expression in plain words for the shapes the admin
 * app's schedule presets produce (plus a few common variants). Returns null
 * for anything it can't say simply — callers fall back to generic copy.
 */
export function describeSchedule(cron: string | undefined): string | null {
  if (!cron) return null
  const fields = cron.trim().split(/\s+/)
  if (fields.length !== 5) return null
  const [min, hour, dom, month, dow] = fields as [string, string, string, string, string]
  if (month !== '*') return null

  const minNum = /^\d{1,2}$/.test(min) ? Number(min) : null
  const hourNum = /^\d{1,2}$/.test(hour) ? Number(hour) : null
  if (minNum !== null && (minNum < 0 || minNum > 59)) return null
  if (hourNum !== null && (hourNum < 0 || hourNum > 23)) return null

  // Every N minutes / hourly.
  if (hour === '*' && dom === '*' && dow === '*') {
    const every = /^\*\/(\d{1,2})$/.exec(min)
    if (every) return `Runs every ${every[1]} minutes`
    if (minNum !== null) return `Runs hourly at ${minNum} past the hour`
    return null
  }
  if (minNum === null || hourNum === null) return null
  const at = timeOfDay(hourNum, minNum)

  // Daily / weekdays / one weekday.
  if (dom === '*') {
    if (dow === '*') return `Runs every day at ${at}`
    if (dow === '1-5') return `Runs weekdays at ${at}`
    if (/^[0-6]$/.test(dow)) return `Runs every ${WEEKDAY_NAMES[Number(dow)]} at ${at}`
    return null
  }

  // Monthly on a fixed day.
  if (dow === '*' && /^\d{1,2}$/.test(dom)) {
    const day = Number(dom)
    if (day < 1 || day > 31) return null
    return `Runs monthly on day ${day} at ${at}`
  }
  return null
}
